import {createApi} from '@reduxjs/toolkit/query/react';
import {AxiosRequestHeaders} from 'axios';

import {invalidateDomainMonitors} from 'entities/monitor';

import httpBaseQuery, {IHttpBaseQueryMeta} from '../../../shared/api/rtk-query/httpBaseQuery';
import {ApiUrls} from '../../../shared/config/model/urls';
import IHttpMonitorOutput, {IHttpMonitorItem} from '../model/HttpMonitor/IHttpMonitor';

import {buildQuery} from '../../../shared/api/query';
import {providesListUuid} from '../../../shared/api/rtk-query/RTKQueryHelper';
import UpdateHttpMonitorType from '../model/HttpMonitor/UpdateHttpMonitorType';
import HttpMonitorPayload from '../model/HttpMonitor/HttpMonitorPayload';
import IHydraResponse from '../../../shared/api/model/HydraResponse';
import IPagination from '../../../shared/api/model/IPagination';
import {paginationAdapter} from '../../../shared/api/paginationHelper';
import IHttpMonitorAvgResponseTime from '../model/HttpMonitor/IHttpMonitorAvgResponseTime';
import ISummaryHttpMonitors from '../model/HttpMonitor/ISummaryHttpMonitors';
import ICollectionParams from '../../../shared/api/model/ICollectionParams';
import IOrderBy from '../../../shared/model/IOrderBy';
import IHttpMonitorResult from '../model/HttpMonitor/IHttpMonitorResult';
import IApiViolation from '../../../shared/api/model/IApiViolation';

export interface IHttpMonitorMutationError {
    message: unknown,
    violations?: IApiViolation[],
}

const withViolations = (
    baseQueryReturnValue: unknown,
    meta: IHttpBaseQueryMeta | undefined,
): IHttpMonitorMutationError => ({
    message: baseQueryReturnValue,
    violations: meta?.violations,
});

export const httpMonitorAPI = createApi({
    reducerPath: 'httpMonitorAPI',
    baseQuery: httpBaseQuery(),
    tagTypes: ['HttpMonitor', 'SummaryHttpMonitors'],
    refetchOnMountOrArgChange: 60,
    refetchOnFocus: true,
    refetchOnReconnect: true,
    endpoints: (build) => ({
        addHttpMonitor: build.mutation<IHttpMonitorOutput, HttpMonitorPayload>({
            query: (data) => ({
                path: ApiUrls.HTTP_MONITORS(),
                method: 'POST',
                data,
                silent: true,
            }),
            transformErrorResponse: withViolations,
            async onQueryStarted(arg, {dispatch, queryFulfilled}) {
                await queryFulfilled;
                invalidateDomainMonitors(dispatch);
            },
            invalidatesTags: [{ type: 'HttpMonitor', id: 'LIST' }, 'SummaryHttpMonitors'],
        }),
        getHttpMonitors: build.query<IPagination<IHttpMonitorOutput>, ICollectionParams>({
            query: ({page, sort, filters, limit}) => ({
                path: buildQuery(ApiUrls.HTTP_MONITORS(page, sort, filters, limit)),
                headers: {Accept: 'application/ld+json'} as unknown as AxiosRequestHeaders,
            }),
            providesTags: (result) => providesListUuid(result?.items, 'HttpMonitor'),
            transformResponse(
                rawResult: IHydraResponse<IHttpMonitorOutput>,
            ): Promise<IPagination<IHttpMonitorOutput>> | IPagination<IHttpMonitorOutput> {
                return paginationAdapter(rawResult);
            },
        }),
        getHttpMonitor: build.query<IHttpMonitorItem, string>({
            query: (uuid) => ({
                path: ApiUrls.HTTP_MONITOR(uuid),
            }),
            providesTags: (result, error, uuid) => [{ type: 'HttpMonitor', id: uuid }],
        }),
        getHttpMonitorResults: build.query<IPagination<IHttpMonitorResult>,
            { uuid: string; page?: number, filters?: string[] }>({
                query: ({uuid, page, filters}) => ({
                    path: ApiUrls.HTTP_MONITOR_RESULTS(uuid, page, filters),
                    headers: {Accept: 'application/ld+json'} as unknown as AxiosRequestHeaders,
                }),
                transformResponse(
                    rawResult: IHydraResponse<IHttpMonitorResult>,
                ): Promise<IPagination<IHttpMonitorResult>> | IPagination<IHttpMonitorResult> {
                    return paginationAdapter(rawResult);
                },
                providesTags: (result, error, { uuid }) => [
                    { type: 'HttpMonitor', id: uuid },
                    { type: 'HttpMonitor', id: `${uuid}-results` },
                ],
            }),
        checkHttpMonitorNow: build.mutation<void, string>({
            query: (uuid) => ({
                path: ApiUrls.HTTP_MONITOR_CHECK_NOW(uuid),
                method: 'GET',
            }),
            invalidatesTags: (result, error, uuid) => [
                { type: 'HttpMonitor', id: uuid },
                { type: 'HttpMonitor', id: 'LIST' },
            ],
        }),
        getHttpMonitorAvgResponseTime: build.query<IHttpMonitorAvgResponseTime[], string>({
            query: (uuid) => ({
                path: ApiUrls.HTTP_MONITOR_AVG_RESPONSE_TIME(uuid),
            }),
        }),
        updateHttpMonitor: build.mutation<IHttpMonitorOutput, UpdateHttpMonitorType>({
            query: ({uuid, ...data}) => ({
                path: ApiUrls.HTTP_MONITOR(uuid ?? ''),
                method: 'PUT',
                data,
                silent: true,
            }),
            transformErrorResponse: withViolations,
            async onQueryStarted({ uuid, ...patch }, { dispatch }) {
                dispatch(
                    httpMonitorAPI.util.updateQueryData('getHttpMonitor', uuid ?? '', (draft) => {
                        Object.assign(draft, patch);
                    }),
                );
            },
            invalidatesTags: [{ type: 'HttpMonitor' }, 'SummaryHttpMonitors'],
        }),
        deleteHttpMonitor: build.mutation<void,  string>({
            query: (uuid) => ({
                path: ApiUrls.HTTP_MONITOR(uuid),
                method: 'DELETE',
            }),
            invalidatesTags: () => [
                { type: 'HttpMonitor', id: 'LIST' },
                'SummaryHttpMonitors',
            ],
        }),
        getDomainOfHttpMonitor: build.query<{domain: string}, string>({
            query: (uuid) => ({
                path: ApiUrls.HTTP_MONITORS_UNSUBSCRIBE(uuid),
                method: 'GET',
                isAnonymous: true,
            }),
        }),
        unsubscribeHttpMonitor: build.query<{success: boolean}, string>({
            query: (uuid) => ({
                path: ApiUrls.HTTP_MONITORS_UNSUBSCRIBE(uuid),
                method: 'POST',
                isAnonymous: true,
                data: {},
            }),
        }),
        getSummaryHttpMonitors: build.query<ISummaryHttpMonitors, void>({
            query: () => ({
                path: ApiUrls.SUMMARY_HTTP_MONITORS,
            }),
            providesTags: ['SummaryHttpMonitors'],
        }),
        getAllHttpMonitorIds: build.query<string[], { sort?: IOrderBy; filters?: string[] }>({
            query: ({ sort, filters }) => ({
                path: ApiUrls.HTTP_MONITORS_IDS(sort, filters),
                headers: { Accept: 'application/json' } as unknown as AxiosRequestHeaders,
            }),
        }),
        startHttpMonitor: build.mutation<void, string>({
            query: (uuid) => ({
                path: ApiUrls.HTTP_MONITOR_START(uuid),
                method: 'PUT',
            }),
            invalidatesTags: (result, error, uuid) => [
                { type: 'HttpMonitor', id: uuid },
                { type: 'HttpMonitor', id: 'LIST' },
                'SummaryHttpMonitors',
            ],
        }),
        stopHttpMonitor: build.mutation<void, string>({
            query: (uuid) => ({
                path: ApiUrls.HTTP_MONITOR_STOP(uuid),
                method: 'PUT',
            }),
            invalidatesTags: (result, error, uuid) => [
                { type: 'HttpMonitor', id: uuid },
                { type: 'HttpMonitor', id: 'LIST' },
                'SummaryHttpMonitors',
            ],
        }),
    }),
});